import Employee from "../models/Employee";
import ClaimDetails from "../models/ClaimDetails";
import LeaveDetails from "../models/LeaveDetails";
import ClaimLeaveData from "../models/ClaimLeaveData";

import apiSlice from "./apiSlice";
import config from "../config";
import { removeInErrorOdsEmployeeRecords } from "../utils/inErrorRecords";

import { setOdsEmployeesData } from "./employeeClaimLeaveSlice";

const API_VERSION = process.env.REACT_APP_API_VERSION;
const ENV_SUFFIX = config.REACT_APP_ENV_NAME;

const getIsODSEnabled = (state) => state.app.flags.isODSEnabled;

const hasValue = (value) =>
    value !== null && value !== undefined && value !== "";

const employeeApiUrl = (path = "") =>
    `${config.REACT_APP_API_URL}/employees/${API_VERSION}${path}${ENV_SUFFIX}`;

const enterpriseApiUrl = (path = "") =>
    `${config.REACT_APP_ENTERPRISE_INFO_API_URL}${path}${ENV_SUFFIX}`;

const createDualApiRequest =
    ({ odsRequest, legacyRequest }) =>
    (args, isODS) =>
        isODS ? odsRequest(args) : legacyRequest(args);

const getEmployeeSearchHeaders = (
    { ssn, lastName, firstName, claimId, leaveId },
    isODS
) => {
    const values = [
        [isODS ? "x-unum-param-taxIdentificationNumber" : "x-unum-SSN", ssn],
        [isODS ? "x-unum-param-lastName" : "x-unum-LastName", lastName],
        [isODS ? "x-unum-param-firstName" : "x-unum-FirstName", firstName],
        [isODS ? "x-unum-param-claimId" : "x-unum-ClaimId", claimId],
        [isODS ? "x-unum-param-leaveId" : "x-unum-LeaveId", leaveId],
    ];

    return values.reduce((headers, [key, value]) => {
        if (hasValue(value)) {
            headers[key] = value;
        }

        return headers;
    }, {});
};

const cacheOdsEmployeesData = (api, response) => {
    const employees = response?.EmployeeDetail;

    if (!Array.isArray(employees) || employees.length === 0) {
        return;
    }

    const keys = Employee.getOdsEmployeeKeys(employees);
    const employeeMap = employees.reduce((acc, employee, index) => {
        const key = keys[index];

        if (key) {
            acc[key] = employee;
        }

        return acc;
    }, {});

    api.dispatch(setOdsEmployeesData(employeeMap));
};

const createFlaggedQuery = ({ buildRequest, transform, transforms }) => {
    return async (arg, api, _, baseQuery) => {
        const isODSEnabled = getIsODSEnabled(api.getState());

        let normalizedTransforms = [];

        if (Array.isArray(transforms)) {
            normalizedTransforms = transforms;
        }

        if (!Array.isArray(transforms) && typeof transform === "function") {
            normalizedTransforms = [transform];
        }

        const result = await baseQuery(buildRequest(arg, isODSEnabled));

        if (result.error) {
            return { error: result.error };
        }

        const context = {
            arg,
            api,
            isODSEnabled,
            rawResponse: result.data,
        };

        const transformedData = normalizedTransforms.reduce(
            (data, transformer) => transformer(data, context),
            result.data
        );

        return {
            data: transformedData,
        };
    };
};

const buildGetEmployeeLeaveDetailsRequest = createDualApiRequest({
    odsRequest: ({ organizationId, leaveNo }) => ({
        url: enterpriseApiUrl(`leaves/v1/${leaveNo}`),
        method: "GET",
        params: {
            organizationId: organizationId
                ? organizationId.toUpperCase()
                : undefined,
        },
    }),

    legacyRequest: ({ employeeId, leaveNo, organizationId }) => ({
        url: employeeApiUrl(`/${employeeId}/leaves/${leaveNo}`),
        method: "GET",
        params: {
            OrgId: organizationId,
        },
    }),
});

const buildGetEmployeeClaimDetailsRequest = createDualApiRequest({
    odsRequest: ({ organizationId, claimNo }) => ({
        url: enterpriseApiUrl(`claims/v1/${claimNo}`),
        method: "GET",
        params: {
            organizationId: organizationId
                ? organizationId.toUpperCase()
                : undefined,
        },
    }),

    legacyRequest: ({ employeeId, claimNo, organizationId }) => ({
        url: employeeApiUrl(`/${employeeId}/claims/${claimNo}`),
        method: "GET",
        params: {
            OrgId: organizationId,
        },
    }),
});

const buildGetEmployeesRequest = createDualApiRequest({
    odsRequest: ({ organizationId, filters }) => {
        const employeeId = filters?.eeid?.trim();

        const params = {
            organizationId: organizationId.toUpperCase(),
            count: 100,
        };

        if (employeeId) {
            params.employeeId = employeeId;
        }

        return {
            url: enterpriseApiUrl(`employees/${API_VERSION}`),
            method: "GET",
            params,
            headers: getEmployeeSearchHeaders(
                {
                    ...filters,
                    claimId: filters.claimNumber,
                    leaveId: filters.leaveNumber,
                },
                true
            ),
        };
    },

    legacyRequest: ({ organizationId, filters }) => ({
        url: employeeApiUrl(),
        method: "GET",
        params: {
            OrgId: organizationId,
            LeaveNo: filters.leaveNumber || null,
            ClaimNo: filters.claimNumber || null,
            OrgName: null,
            PolicyId: null,
            EEID: filters.eeid || null,
        },
        headers: {
            "x-unum-SSN": filters.ssn || "null",
            "x-unum-LastName": filters.lastName || "null",
            "x-unum-FirstName": filters.firstName || "null",
        },
    }),
});

const cacheAndNormalizeResponse = (response, context) => {
    if (!context.isODSEnabled) {
        return response;
    }

    const validResponse = removeInErrorOdsEmployeeRecords(response);

    cacheOdsEmployeesData(context.api, validResponse);
    cacheOdsEmployeesDataWithFallbackKey(context.api, validResponse);

    return validResponse;
};

const cacheOdsEmployeesDataWithFallbackKey = (api, response) => {
    const rawList = response?.EmployeeDetail || [];
    const odsMap = {};
    const keys = Employee.getOdsEmployeeKeys(rawList);

    rawList.forEach((emp, index) => {
        const key = keys[index];
        if (key) {
            odsMap[key] = emp;
        }
    });

    api.dispatch(setOdsEmployeesData(odsMap));
};

const formatEmployeeSearchResponse = (response, context) => {
    if (context.isODSEnabled) {
        return Employee.formatOdsSearchResult(response);
    }

    return Employee.format(response);
};

const employeesApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getEmployees: builder.query({
            queryFn: createFlaggedQuery({
                buildRequest: buildGetEmployeesRequest,
                transforms: [
                    cacheAndNormalizeResponse,
                    formatEmployeeSearchResponse,
                ],
            }),

            invalidatesTags: ["employees"],
        }),

        getEmployeeDetails: builder.query({
            query: ({ employeeId, organizationId }) => ({
                url: employeeApiUrl(`/${employeeId}/absences`),
                method: "GET",
                params: {
                    OrgId: organizationId,
                    IsClaimOrLeave: 3,
                    isInternalUser: 1,
                },
            }),

            invalidatesTags: ["employeeDetails"],

            transformResponse: (response) => {
                const claimLeaveData = ClaimLeaveData.format(response);

                const employeeDetails =
                    response.EmployeeDetails.find(
                        (employee) => employee.SSN !== null
                    ) ?? response.EmployeeDetails[0];

                return {
                    EmployeeDetails: employeeDetails,
                    ClaimLeaveData: claimLeaveData,
                };
            },
        }),

        getEmployeeLeaveDetails: builder.query({
            queryFn: createFlaggedQuery({
                buildRequest: buildGetEmployeeLeaveDetailsRequest,

                transform: (data, context) => {
                    if (context.isODSEnabled) return LeaveDetails.format(data);
                    const claimLeaveData = ClaimLeaveData.format(data);
                    const employeeDetails = Array.isArray(data.EmployeeDetails)
                        ? (data.EmployeeDetails.find((e) => e.SSN !== null) ??
                          data.EmployeeDetails[0])
                        : data.EmployeeDetails;
                    return {
                        ...data,
                        EmployeeDetails: employeeDetails,
                        ClaimLeaveData: claimLeaveData,
                    };
                },
            }),

            invalidatesTags: ["leaveDetails"],
        }),

        getEmployeeClaimDetails: builder.query({
            queryFn: createFlaggedQuery({
                buildRequest: buildGetEmployeeClaimDetailsRequest,

                transform: (data, context) =>
                    context.isODSEnabled
                        ? ClaimDetails.formatODS(data)
                        : ClaimDetails.format(data),
            }),

            invalidatesTags: ["claimDetails"],
        }),

        getOrganizationAccess: builder.query({
            query: ({ organizationIds }) => ({
                url: employeeApiUrl("/user-organization-access"),
                method: "GET",
                params: {
                    OrgId: organizationIds,
                },
            }),
        }),
    }),

    overrideExisting: false,
});

export const {
    useGetEmployeesQuery,
    useLazyGetEmployeesQuery,
    useGetEmployeeDetailsQuery,
    useLazyGetEmployeeDetailsQuery,
    useGetEmployeeLeaveDetailsQuery,
    useLazyGetEmployeeLeaveDetailsQuery,
    useGetEmployeeClaimDetailsQuery,
    useLazyGetEmployeeClaimDetailsQuery,
    useGetOrganizationAccessQuery,
    useLazyGetOrganizationAccessQuery,
} = employeesApiSlice;

export {
    buildGetEmployeesRequest,
    getIsODSEnabled,
    getEmployeeSearchHeaders,
    createFlaggedQuery,
    cacheAndNormalizeResponse,
    formatEmployeeSearchResponse,
    cacheOdsEmployeesDataWithFallbackKey,
};

export default employeesApiSlice;
